import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import type { Image } from '../../services/api';
import api from '../../services/api';

import styles from './BlogPostEditor.module.css';
import ImagePickerModal from './ImagePickerModal';
import TipTapEditor from './TipTapEditor';

/**
 * Generate slug from title
 */
const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/**
 * BlogPostEditor Component
 *
 * Editor for creating and editing individual blog posts.
 * Features:
 * - Title input with auto-slug generation
 * - Excerpt textarea for list preview
 * - Featured image picker
 * - TipTap rich text editor for content
 * - Draft/Published status toggle
 * - Unsaved changes warning
 */
const BlogPostEditor: React.FC = () => {
  const { slug: pageSlug, postId } = useParams<{ slug: string; postId: string }>();
  const navigate = useNavigate();
  const isNew = postId === 'new';

  // Form state
  const [title, setTitle] = useState('');
  const [postSlug, setPostSlug] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [featuredImage, setFeaturedImage] = useState<{
    imageId: string;
    url: string;
    alt?: string;
  } | null>(null);
  const [status, setStatus] = useState<'draft' | 'published'>('draft');

  // UI state
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [showInlineImagePicker, setShowInlineImagePicker] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  /**
   * Fetch existing post data
   */
  const fetchPost = useCallback(async () => {
    if (isNew || !postId) return;

    try {
      setLoading(true);
      // We need to fetch the post - use admin endpoint to get all posts and find the one
      const posts = await api.blog.getAdminPosts(pageSlug!);
      const post = posts.find((p) => p._id === postId);

      if (post) {
        setTitle(post.title);
        setPostSlug(post.slug);
        setExcerpt(post.excerpt ?? '');
        setContent(post.content);
        setFeaturedImage(post.featuredImage ?? null);
        setStatus(post.status);
        setSlugManuallyEdited(true); // Don't auto-update slug for existing posts
      } else {
        toast.error('Post not found');
        navigate(`/admin/content/${pageSlug}`);
      }
    } catch (error) {
      console.error('Failed to fetch post:', error);
      toast.error('Failed to load post');
      navigate(`/admin/content/${pageSlug}`);
    } finally {
      setLoading(false);
    }
  }, [isNew, postId, pageSlug, navigate]);

  // Initial fetch
  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  // Auto-generate slug from title (only for new posts or if not manually edited)
  useEffect(() => {
    if (!slugManuallyEdited && title) {
      setPostSlug(generateSlug(title));
    }
  }, [title, slugManuallyEdited]);

  // Unsaved changes warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  /**
   * Handle title change
   */
  const handleTitleChange = (value: string) => {
    setTitle(value);
    setHasChanges(true);
  };

  /**
   * Handle slug change (manual edit)
   */
  const handleSlugChange = (value: string) => {
    setPostSlug(generateSlug(value));
    setSlugManuallyEdited(true);
    setHasChanges(true);
  };

  /**
   * Handle content change
   */
  const handleContentChange = (html: string) => {
    setContent(html);
    setHasChanges(true);
  };

  /**
   * Handle featured image selection
   */
  const handleImageSelect = (image: Image) => {
    setFeaturedImage({
      imageId: image._id,
      url: image.url,
      alt: image.alt ?? image.title,
    });
    setHasChanges(true);
    setShowImagePicker(false);
  };

  /**
   * Remove featured image
   */
  const handleRemoveFeaturedImage = () => {
    setFeaturedImage(null);
    setHasChanges(true);
  };

  /**
   * Handle inline image selection (for inserting into content)
   */
  const handleInlineImageSelect = (image: Image) => {
    const insertImage = (
      window as unknown as { __tiptapInsertImage?: (url: string, alt?: string) => void }
    ).__tiptapInsertImage;
    if (insertImage) {
      insertImage(image.url, image.alt ?? image.title);
    }
    setShowInlineImagePicker(false);
  };

  /**
   * Handle save
   */
  const handleSave = async () => {
    // Validation
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!postSlug.trim()) {
      toast.error('Slug is required');
      return;
    }

    try {
      setSaving(true);

      const postData = {
        title: title.trim(),
        slug: postSlug.trim(),
        excerpt: excerpt.trim() || undefined,
        content,
        featuredImage: featuredImage ?? undefined,
        status,
        pageSlug: pageSlug!,
      };

      if (isNew) {
        await api.blog.create(postData);
        toast.success('Post created');
      } else {
        await api.blog.update(postId!, postData);
        toast.success('Post saved');
      }

      setHasChanges(false);
      navigate(`/admin/content/${pageSlug}`);
    } catch (error) {
      console.error('Failed to save post:', error);
      toast.error('Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle cancel
   */
  const handleCancel = () => {
    if (hasChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        return;
      }
    }
    navigate(`/admin/content/${pageSlug}`);
  };

  if (loading) {
    return (
      <div className={styles.blogPostEditor}>
        <div className={styles.loading}>Loading post...</div>
      </div>
    );
  }

  return (
    <div className={styles.blogPostEditor}>
      {/* Header */}
      <div className={styles.header}>
        <h2>{isNew ? 'New Post' : 'Edit Post'}</h2>
        <div className={styles.headerActions}>
          <button className={styles.cancelButton} onClick={handleCancel} disabled={saving}>
            Cancel
          </button>
          <button className={styles.saveButton} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Post'}
          </button>
        </div>
      </div>

      {/* Main Form */}
      <div className={styles.formContainer}>
        {/* Left Column - Main Content */}
        <div className={styles.mainColumn}>
          {/* Title */}
          <div className={styles.formGroup}>
            <label htmlFor="title">Title *</label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Enter post title..."
              className={styles.titleInput}
            />
          </div>

          {/* Slug */}
          <div className={styles.formGroup}>
            <label htmlFor="slug">Slug</label>
            <div className={styles.slugContainer}>
              <span className={styles.slugPrefix}>/blog/</span>
              <input
                id="slug"
                type="text"
                value={postSlug}
                onChange={(e) => handleSlugChange(e.target.value)}
                placeholder="post-slug"
                className={styles.slugInput}
              />
            </div>
          </div>

          {/* Rich Text Editor */}
          <div className={styles.formGroup}>
            <label>Content</label>
            <TipTapEditor
              content={content}
              onChange={handleContentChange}
              placeholder="Write your post content here..."
              onImageInsert={() => setShowInlineImagePicker(true)}
            />
          </div>
        </div>

        {/* Right Column - Sidebar */}
        <div className={styles.sideColumn}>
          {/* Status */}
          <div className={styles.sideCard}>
            <h3>Status</h3>
            <div className={styles.statusToggle}>
              <button
                className={`${styles.statusButton} ${status === 'draft' ? styles.active : ''}`}
                onClick={() => {
                  setStatus('draft');
                  setHasChanges(true);
                }}
              >
                Draft
              </button>
              <button
                className={`${styles.statusButton} ${status === 'published' ? styles.active : ''}`}
                onClick={() => {
                  setStatus('published');
                  setHasChanges(true);
                }}
              >
                Published
              </button>
            </div>
          </div>

          {/* Featured Image */}
          <div className={styles.sideCard}>
            <h3>Featured Image</h3>
            {featuredImage ? (
              <div className={styles.featuredImagePreview}>
                <img src={featuredImage.url} alt={featuredImage.alt ?? 'Featured'} />
                <div className={styles.featuredImageActions}>
                  <button
                    className={styles.changeImageButton}
                    onClick={() => setShowImagePicker(true)}
                  >
                    Change
                  </button>
                  <button
                    className={styles.removeImageButton}
                    onClick={handleRemoveFeaturedImage}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <button
                className={styles.addImageButton}
                onClick={() => setShowImagePicker(true)}
              >
                + Add Featured Image
              </button>
            )}
          </div>

          {/* Excerpt */}
          <div className={styles.sideCard}>
            <h3>Excerpt</h3>
            <textarea
              value={excerpt}
              onChange={(e) => {
                setExcerpt(e.target.value);
                setHasChanges(true);
              }}
              placeholder="Brief description for post list..."
              className={styles.excerptTextarea}
              rows={4}
            />
            <p className={styles.excerptHint}>
              Shown in post listings. Leave empty to auto-generate from content.
            </p>
          </div>
        </div>
      </div>

      {/* Featured Image Picker Modal */}
      <ImagePickerModal
        isOpen={showImagePicker}
        onClose={() => setShowImagePicker(false)}
        onSelect={handleImageSelect}
        title="Select Featured Image"
      />

      {/* Inline Image Picker Modal (for content) */}
      <ImagePickerModal
        isOpen={showInlineImagePicker}
        onClose={() => setShowInlineImagePicker(false)}
        onSelect={handleInlineImageSelect}
        title="Insert Image"
      />
    </div>
  );
};

export default BlogPostEditor;
